// src/componentes/Comisiones/ComisionesDashboard.jsx
// F6 — Vista admin del módulo Comisiones.
// Muestra totalizadores, tabla filtrable de todas las comisiones y exporta Excel.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FaDownload, FaFilter, FaUserTie } from "react-icons/fa";
import ExcelJS from "exceljs";
import { listarComisiones } from "../../services/comisionesService";
import notify from "../common/notify";
import "./ComisionesDashboard.css";

// ── Helpers ──────────────────────────────────────────────────────────────────

const fmt = (n) =>
  new Intl.NumberFormat("es-MX", {
    style: "currency",
    currency: "MXN",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(Number(n || 0));

const ESQUEMA_LABELS = {
  "enganche-100": "100% al enganche",
  "enganche-50-mensualidades-50": "50/50 mensualidades",
  "escalonado-por-pagos": "Escalonado",
  "custom": "Personalizado",
};

const ESTADO_BADGE = {
  activa:              { label: "Activa",            css: "cd-badge--activa" },
  parcialmentePagada:  { label: "Parcial",           css: "cd-badge--parcial" },
  totalmenteDevengada: { label: "Dev. completo",     css: "cd-badge--devengada" },
  totalmentePagada:    { label: "Pagada",            css: "cd-badge--totalmentePagada" },
  cancelada:           { label: "Cancelada",         css: "cd-badge--cancelada" },
};

function estadoBadge(estado) {
  const conf = ESTADO_BADGE[estado] || { label: estado || "—", css: "cd-badge--activa" };
  return <span className={`cd-badge ${conf.css}`}>{conf.label}</span>;
}

function formatFecha(value) {
  if (!value) return "—";
  try {
    const d = value?.toDate ? value.toDate() : new Date(value);
    return d.toLocaleDateString("es-MX", { day: "2-digit", month: "short", year: "numeric" });
  } catch { return "—"; }
}

// ── Excel export ─────────────────────────────────────────────────────────────

async function exportarExcel(comisiones) {
  const wb = new ExcelJS.Workbook();
  wb.creator = "Sistema K&E";
  wb.created = new Date();

  // ─ Hoja 1: Resumen ─
  const wsRes = wb.addWorksheet("Resumen");
  wsRes.columns = [
    { header: "Métrica", key: "metrica", width: 28 },
    { header: "Valor", key: "valor", width: 18 },
  ];
  wsRes.getRow(1).font = { bold: true, color: { argb: "FFFFFFFF" } };
  wsRes.getRow(1).fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF2563EB" } };

  const totalComis   = comisiones.length;
  // Excluimos canceladas de monto/devengado/saldo: no representan compromiso
  // ni adeudo al comisionista. Pagado se mantiene porque el dinero ya salio.
  const vigentes     = comisiones.filter((c) => c.estado !== "cancelada");
  const totalMonto   = vigentes.reduce((a, c) => a + Number(c.montoTotal || 0), 0);
  const totalDev     = vigentes.reduce((a, c) => a + Number(c.devengado || 0), 0);
  const totalPagado  = comisiones.reduce((a, c) => a + Number(c.pagado || 0), 0);
  const totalSaldo   = vigentes.reduce((a, c) => a + Number(c.saldo || 0), 0);

  wsRes.addRows([
    { metrica: "Total comisiones", valor: totalComis },
    { metrica: "Monto total comprometido", valor: totalMonto },
    { metrica: "Total devengado", valor: totalDev },
    { metrica: "Total pagado", valor: totalPagado },
    { metrica: "Saldo pendiente", valor: totalSaldo },
    { metrica: "Fecha de exportación", valor: new Date().toLocaleDateString("es-MX") },
  ]);

  ["B2","B3","B4","B5","B6"].forEach((cell) => {
    wsRes.getCell(cell).numFmt = '"$"#,##0';
  });

  // ─ Hoja 2: Comisiones ─
  const ws = wb.addWorksheet("Comisiones");
  ws.columns = [
    { header: "ID",                key: "id",                width: 22 },
    { header: "Propiedad",         key: "propiedadFolio",    width: 14 },
    { header: "Descripción",       key: "propiedadDesc",     width: 28 },
    { header: "Fraccionamiento",   key: "fraccionamiento",   width: 22 },
    { header: "Cliente",           key: "cliente",           width: 24 },
    { header: "Comisionista",      key: "comisionista",      width: 22 },
    { header: "Tipo cálculo",      key: "tipoCalculo",       width: 14 },
    { header: "Porcentaje",        key: "porcentaje",        width: 12 },
    { header: "Monto total",       key: "montoTotal",        width: 16 },
    { header: "Devengado",         key: "devengado",         width: 16 },
    { header: "Pagado",            key: "pagado",            width: 16 },
    { header: "Saldo",             key: "saldo",             width: 16 },
    { header: "Esquema",           key: "esquema",           width: 20 },
    { header: "Estado",            key: "estado",            width: 20 },
    { header: "Hitos totales",     key: "hitosTotal",        width: 14 },
    { header: "Hitos devengados",  key: "hitosDevengados",   width: 16 },
    { header: "Hitos pagados",     key: "hitosPagados",      width: 14 },
    { header: "Fecha creación",    key: "fechaCreacion",     width: 18 },
  ];

  // Encabezado con estilo
  ws.getRow(1).font = { bold: true, color: { argb: "FFFFFFFF" } };
  ws.getRow(1).fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF1E40AF" } };
  ws.getRow(1).alignment = { vertical: "middle" };
  ws.getRow(1).height = 20;

  for (const c of comisiones) {
    const hitos = Array.isArray(c.hitos) ? c.hitos : [];
    ws.addRow({
      id:              c.id,
      propiedadFolio:  c.propiedadFolio || "",
      propiedadDesc:   c.propiedadDescripcion || c.propiedadFolio || "",
      fraccionamiento: c.fraccionamientoNombre || "",
      cliente:         c.clienteNombre || "",
      comisionista:    c.comisionistaNombre || "",
      tipoCalculo:     c.tipoCalculo === "monto_fijo" ? "Monto fijo" : "Porcentaje",
      porcentaje:      c.tipoCalculo === "monto_fijo" ? "" : `${((Number(c.porcentaje || 0)) * 100).toFixed(2)}%`,
      montoTotal:      Number(c.montoTotal || 0),
      devengado:       Number(c.devengado || 0),
      pagado:          Number(c.pagado || 0),
      saldo:           Number(c.saldo || 0),
      esquema:         ESQUEMA_LABELS[c.esquemaPago] || c.esquemaPago || "",
      estado:          ESTADO_BADGE[c.estado]?.label || c.estado || "",
      hitosTotal:      hitos.length,
      hitosDevengados: hitos.filter((h) => h.devengado).length,
      hitosPagados:    hitos.filter((h) => h.pagado).length,
      fechaCreacion:   formatFecha(c.creado),
    });
  }

  // Formato moneda para columnas I-L (índices 9-12, 1-based)
  ["I","J","K","L"].forEach((col) => {
    ws.getColumn(col).numFmt = '"$"#,##0.00';
  });

  // Zebra
  ws.eachRow((row, rowNum) => {
    if (rowNum > 1 && rowNum % 2 === 0) {
      row.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFF8FAFC" } };
    }
  });

  // ─ Hoja 3: Hitos detallados ─
  const wsH = wb.addWorksheet("Hitos");
  wsH.columns = [
    { header: "Comisión ID",      key: "comisionId",   width: 22 },
    { header: "Comisionista",     key: "comisionista", width: 22 },
    { header: "Propiedad",        key: "propiedad",    width: 14 },
    { header: "Hito",             key: "etiqueta",     width: 20 },
    { header: "Trigger",          key: "trigger",      width: 22 },
    { header: "Monto",            key: "monto",        width: 14 },
    { header: "Devengado",        key: "devengado",    width: 12 },
    { header: "Pagado",           key: "pagado",       width: 12 },
    { header: "CxP ID",           key: "cxpId",        width: 22 },
    { header: "Fecha devengado",  key: "fechaDev",     width: 16 },
    { header: "Fecha pagado",     key: "fechaPag",     width: 16 },
  ];
  wsH.getRow(1).font = { bold: true, color: { argb: "FFFFFFFF" } };
  wsH.getRow(1).fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF475569" } };

  for (const c of comisiones) {
    for (const h of (c.hitos || [])) {
      wsH.addRow({
        comisionId:   c.id,
        comisionista: c.comisionistaNombre || "",
        propiedad:    c.propiedadFolio || "",
        etiqueta:     h.etiqueta || "",
        trigger:      h.trigger || "",
        monto:        Number(h.monto || 0),
        devengado:    h.devengado ? "Sí" : "No",
        pagado:       h.pagado ? "Sí" : "No",
        cxpId:        h.cxpId || "",
        fechaDev:     h.fechaDevengado || "",
        fechaPag:     h.fechaPagado || "",
      });
    }
  }
  wsH.getColumn("F").numFmt = '"$"#,##0.00';

  // Generar y descargar
  const buffer = await wb.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `comisiones_${new Date().toISOString().slice(0, 10)}.xlsx`;
  a.click();
  URL.revokeObjectURL(url);
}

// ── Componente ────────────────────────────────────────────────────────────────

export default function ComisionesDashboard() {
  const navigate = useNavigate();
  const [comisiones, setComisiones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exportando, setExportando] = useState(false);

  // Filtros
  const [busqueda, setBusqueda] = useState("");
  const [filtroEstado, setFiltroEstado] = useState("");
  const [filtroComisionista, setFiltroComisionista] = useState("");

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const data = await listarComisiones({ maxItems: 500 });
        if (alive) setComisiones(data);
      } catch (e) {
        console.error(e);
        notify.error("No se pudo cargar la lista de comisiones.");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  // ── Filtrado client-side ─────────────────────────────────────────────────
  const comisionesFiltradas = useMemo(() => {
    const txt = busqueda.trim().toLowerCase();
    return comisiones.filter((c) => {
      if (filtroEstado && c.estado !== filtroEstado) return false;
      if (filtroComisionista && c.comisionistaId !== filtroComisionista) return false;
      if (txt) {
        const haystack = [
          c.propiedadFolio, c.clienteNombre, c.comisionistaNombre,
          c.fraccionamientoNombre, c.id,
        ].join(" ").toLowerCase();
        if (!haystack.includes(txt)) return false;
      }
      return true;
    });
  }, [comisiones, busqueda, filtroEstado, filtroComisionista]);

  // ── Totalizadores (sobre lista completa, no filtrada) ────────────────────
  // Comisiones canceladas NO se cuentan en monto comprometido, devengado ni
  // saldo pendiente — ya no son obligaciones reales con el comisionista.
  // pagado se mantiene porque el dinero ya salio del banco.
  const totales = useMemo(() => {
    const vigentes = comisiones.filter((c) => c.estado !== "cancelada");
    return {
      count:    comisiones.length,
      monto:    vigentes.reduce((a, c) => a + Number(c.montoTotal || 0), 0),
      devengado:vigentes.reduce((a, c) => a + Number(c.devengado  || 0), 0),
      pagado:   comisiones.reduce((a, c) => a + Number(c.pagado   || 0), 0),
      saldo:    vigentes.reduce((a, c) => a + Number(c.saldo      || 0), 0),
    };
  }, [comisiones]);

  // ── Lista única de comisionistas para el filtro ──────────────────────────
  const comisionistasOpciones = useMemo(() => {
    const map = new Map();
    for (const c of comisiones) {
      if (c.comisionistaId && !map.has(c.comisionistaId)) {
        map.set(c.comisionistaId, c.comisionistaNombre || c.comisionistaId);
      }
    }
    return [...map.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  }, [comisiones]);

  const handleExportar = useCallback(async () => {
    if (exportando || !comisiones.length) return;
    setExportando(true);
    try {
      await exportarExcel(comisionesFiltradas.length ? comisionesFiltradas : comisiones);
      notify.success("Archivo Excel generado.");
    } catch (e) {
      console.error(e);
      notify.error("No se pudo generar el Excel.");
    } finally {
      setExportando(false);
    }
  }, [exportando, comisiones, comisionesFiltradas]);

  if (loading) {
    return <div className="cd-loading">Cargando comisiones…</div>;
  }

  return (
    <div className="cd-page">
      {/* ── Header ─────────────────────────────────────────────── */}
      <div className="cd-header">
        <h1>Comisiones de venta</h1>
        <div className="cd-header-actions">
          <button
            className="cd-btn"
            onClick={() => navigate("/app/comisionistas")}
          >
            <FaUserTie /> Comisionistas
          </button>
          <button
            className="cd-btn cd-btn--primary"
            onClick={handleExportar}
            disabled={exportando || !comisiones.length}
          >
            <FaDownload /> {exportando ? "Generando…" : "Exportar Excel"}
          </button>
        </div>
      </div>

      {/* ── KPIs ───────────────────────────────────────────────── */}
      <div className="cd-kpis">
        <div className="cd-kpi">
          <div className="cd-kpi-label">Total comisiones</div>
          <div className="cd-kpi-value">{totales.count}</div>
          <div className="cd-kpi-sub">registradas en el sistema</div>
        </div>
        <div className="cd-kpi">
          <div className="cd-kpi-label">Monto comprometido</div>
          <div className="cd-kpi-value">{fmt(totales.monto)}</div>
          <div className="cd-kpi-sub">suma de todas las comisiones</div>
        </div>
        <div className="cd-kpi">
          <div className="cd-kpi-label">Total devengado</div>
          <div className="cd-kpi-value cd-kpi-value--devengado">{fmt(totales.devengado)}</div>
          <div className="cd-kpi-sub">hitos disparados y CxP generada</div>
        </div>
        <div className="cd-kpi">
          <div className="cd-kpi-label">Total pagado</div>
          <div className="cd-kpi-value cd-kpi-value--pagado">{fmt(totales.pagado)}</div>
          <div className="cd-kpi-sub">CxPs liquidadas</div>
        </div>
        <div className="cd-kpi">
          <div className="cd-kpi-label">Saldo pendiente</div>
          <div className="cd-kpi-value cd-kpi-value--saldo">{fmt(totales.saldo)}</div>
          <div className="cd-kpi-sub">por devengar o pagar</div>
        </div>
      </div>

      {/* ── Filtros ─────────────────────────────────────────────── */}
      <div className="cd-filters">
        <FaFilter style={{ color: "#94a3b8" }} />
        <input
          className="cd-search"
          type="text"
          placeholder="Buscar propiedad, cliente, comisionista…"
          value={busqueda}
          onChange={(e) => setBusqueda(e.target.value)}
        />
        <select
          className="cd-select"
          value={filtroEstado}
          onChange={(e) => setFiltroEstado(e.target.value)}
        >
          <option value="">Todos los estados</option>
          <option value="activa">Activa</option>
          <option value="parcialmentePagada">Parcialmente pagada</option>
          <option value="totalmenteDevengada">Dev. completo</option>
          <option value="totalmentePagada">Totalmente pagada</option>
          <option value="cancelada">Cancelada</option>
        </select>
        {comisionistasOpciones.length > 0 && (
          <select
            className="cd-select"
            value={filtroComisionista}
            onChange={(e) => setFiltroComisionista(e.target.value)}
          >
            <option value="">Todos los comisionistas</option>
            {comisionistasOpciones.map(([id, nombre]) => (
              <option key={id} value={id}>{nombre}</option>
            ))}
          </select>
        )}
        <span className="cd-filters-count">
          {comisionesFiltradas.length} resultado{comisionesFiltradas.length !== 1 ? "s" : ""}
        </span>
      </div>

      {/* ── Tabla ───────────────────────────────────────────────── */}
      <div className="cd-table-wrap">
        {comisionesFiltradas.length === 0 ? (
          <div className="cd-empty">
            {comisiones.length === 0
              ? "No hay comisiones registradas."
              : "No hay resultados para los filtros aplicados."}
          </div>
        ) : (
          <table className="cd-table">
            <thead>
              <tr>
                <th>Propiedad</th>
                <th>Cliente</th>
                <th>Comisionista</th>
                <th>Esquema</th>
                <th className="cd-th-right">Total</th>
                <th className="cd-th-right">Devengado</th>
                <th className="cd-th-right">Pagado</th>
                <th className="cd-th-right">Saldo</th>
                <th>Estado</th>
                <th>Creada</th>
              </tr>
            </thead>
            <tbody>
              {comisionesFiltradas.map((c) => (
                <tr
                  key={c.id}
                  style={{ cursor: "pointer" }}
                  onClick={() => navigate(`/app/comisionistas/${c.comisionistaId}`)}
                  title="Ver comisionista"
                >
                  <td className="cd-td-folio">{c.propiedadFolio || "—"}</td>
                  <td className="cd-td-name" title={c.clienteNombre}>{c.clienteNombre || "—"}</td>
                  <td className="cd-td-name" title={c.comisionistaNombre}>{c.comisionistaNombre || "—"}</td>
                  <td>{ESQUEMA_LABELS[c.esquemaPago] || c.esquemaPago || "—"}</td>
                  <td className="cd-td-right">{fmt(c.montoTotal)}</td>
                  <td className="cd-td-right">{fmt(c.devengado)}</td>
                  <td className="cd-td-right">{fmt(c.pagado)}</td>
                  <td className="cd-td-right">{fmt(c.saldo)}</td>
                  <td>{estadoBadge(c.estado)}</td>
                  <td style={{ whiteSpace: "nowrap", color: "#64748b", fontSize: "0.8rem" }}>
                    {formatFecha(c.creado)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
